import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FiX, FiExternalLink, FiLoader } from 'react-icons/fi';
import * as artifactService from '../../services/artifactService';
import { ArtifactViewer } from './ArtifactViewer';
import { ArtifactVersionHistory } from './ArtifactVersionHistory';
import { ArtifactType } from '../../types/artifact';
import { relativeTime } from '../../utils/relativeTime';

interface ArtifactPreviewDrawerProps {
  artifactId: string | null;
  onClose: () => void;
  darkMode: boolean;
}

type DrawerTab = 'content' | 'metadata' | 'history';

// Type colour map
const TYPE_ACCENT: Record<string, string> = {
  text:     '#3b82f6',
  code:     '#10b981',
  image:    '#ec4899',
  idea:     '#f59e0b',
  critique: '#ef4444',
  solution: '#6366f1',
  document: '#6b7280',
  diagram:  '#14b8a6',
  json:     '#f97316',
  yaml:     '#06b6d4',
  toml:     '#84cc16',
  csv:      '#8b5cf6',
  prompt:   '#ec4899',
};

export const ArtifactPreviewDrawer: React.FC<ArtifactPreviewDrawerProps> = ({
  artifactId,
  onClose,
  darkMode,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DrawerTab>('content');
  const isOpen = artifactId !== null;

  // Escape key to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Reset tab when artifact changes
  useEffect(() => {
    if (artifactId) setActiveTab('content');
  }, [artifactId]);

  const { data: artifact, isLoading, isError, refetch } = useQuery(
    ['artifact', artifactId],
    () => artifactService.getArtifact(artifactId!),
    {
      enabled: !!artifactId,
      staleTime: 60_000,
    }
  );

  const accent = artifact ? (TYPE_ACCENT[artifact.type] ?? '#6b7280') : '#6b7280';

  const tabs: { id: DrawerTab; label: string }[] = [
    { id: 'content',  label: 'Content' },
    { id: 'metadata', label: 'Metadata' },
    { id: 'history',  label: 'History' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: 'rgba(0,0,0,0.4)' }}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col shadow-2xl transition-transform duration-300 ease-in-out
          w-full md:w-[480px] ${
          darkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
        } ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Accent bar at top */}
        <div className="h-1 w-full flex-shrink-0" style={{ backgroundColor: accent }} />

        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className="font-semibold text-base truncate mr-2">
            {artifact?.name ?? 'Artifact Preview'}
          </h2>
          <div className="flex items-center gap-2 flex-shrink-0">
            {artifact && (
              <button
                onClick={() => navigate(`/artifacts/${artifact.id}`)}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                  darkMode
                    ? 'bg-blue-700 hover:bg-blue-600 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                <FiExternalLink className="w-3 h-3" />
                Open
              </button>
            )}
            <button
              onClick={onClose}
              className={`p-1.5 rounded transition-colors ${
                darkMode
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Badges row */}
        {artifact && (
          <div className={`flex flex-wrap items-center gap-2 px-4 py-2 border-b flex-shrink-0 ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: `${accent}22`, color: accent }}
            >
              {artifact.type}
            </span>
            {artifact.version > 1 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}>
                v{artifact.version}
              </span>
            )}
            {artifact.phase && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}>
                {artifact.phase}
              </span>
            )}
            {artifact.agent_name && (
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                by {artifact.agent_name}
              </span>
            )}
          </div>
        )}

        {/* Tab bar */}
        <div className={`flex border-b flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? darkMode
                    ? 'border-blue-400 text-blue-400'
                    : 'border-blue-500 text-blue-600'
                  : darkMode
                    ? 'border-transparent text-gray-400 hover:text-gray-200'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <FiLoader className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                Failed to load artifact.
              </p>
              <button
                onClick={() => refetch()}
                className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !isError && artifact && (
            <div className="p-4">
              {activeTab === 'content' && (
                <ArtifactViewer artifact={artifact} />
              )}

              {activeTab === 'metadata' && (
                <div className="space-y-3 text-sm">
                  {[
                    { label: 'ID',         value: artifact.id },
                    { label: 'Type',       value: artifact.type },
                    { label: 'Version',    value: String(artifact.version ?? 1) },
                    { label: 'Phase',      value: artifact.phase ?? '—' },
                    { label: 'Agent',      value: artifact.agent_name ?? '—' },
                    { label: 'Session',    value: (artifact as any).session_id ?? '—' },
                    { label: 'Created',    value: relativeTime(artifact.created_at) },
                    { label: 'Updated',    value: relativeTime(artifact.updated_at) },
                  ].map(({ label, value }) => (
                    <div key={label} className={`flex gap-2 py-1.5 border-b ${
                      darkMode ? 'border-gray-800' : 'border-gray-100'
                    }`}>
                      <span className={`w-20 flex-shrink-0 font-medium text-xs ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>{label}</span>
                      <span className={`text-xs break-all ${
                        darkMode ? 'text-gray-200' : 'text-gray-800'
                      }`}>{value}</span>
                    </div>
                  ))}

                  {/* Tags */}
                  {(artifact as any).tags && (artifact as any).tags.length > 0 && (
                    <div className={`flex gap-2 py-1.5 border-b ${
                      darkMode ? 'border-gray-800' : 'border-gray-100'
                    }`}>
                      <span className={`w-20 flex-shrink-0 font-medium text-xs ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>Tags</span>
                      <div className="flex flex-wrap gap-1">
                        {((artifact as any).tags as string[]).map((tag, i) => (
                          <span key={i} className={`text-xs px-1.5 py-0.5 rounded-full ${
                            darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                          }`}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <ArtifactVersionHistory
                  artifactId={artifact.id}
                  artifactType={artifact.type as ArtifactType}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
